import { makeObservable, observable, action, computed } from 'mobx';
import type RootStore from '../RootStore';
import { ReactionManager } from './ReactionManager';

/**
 * ViewStoreErrorHandling - Standardized error handling pattern for ViewStores
 * Matches the comprehensive error handling approach used in ServiceStore
 */

export interface ViewStoreError {
  name: string;
  message: string;
  operation: string;
  timestamp: number;
  context?: Record<string, unknown>;
}

export interface ErrorHandlingOptions {
  enableErrorNotifications?: boolean;
  enableReactionBatching?: boolean;
  debugMode?: boolean;
}

/**
 * Mixin class that provides standardized error handling for ViewStores
 * Can be used via composition or inheritance
 */
export class ViewStoreErrorHandler {
  // Error state observables - matching ServiceStore pattern
  errorOnLoad: ViewStoreError | null = null;
  errorOnCreate: ViewStoreError | null = null;
  errorOnUpdate: ViewStoreError | null = null;
  errorOnDelete: ViewStoreError | null = null;
  errorOnSubmit: ViewStoreError | null = null;
  generalError: ViewStoreError | null = null;

  // Options
  private enableErrorNotifications: boolean;
  private enableReactionBatching: boolean;
  private debugMode: boolean;
  private reactionManager: ReactionManager | null = null;
  private errorReactionsBatchDispose: (() => void) | null = null;

  constructor(
    private rootStore: RootStore,
    private storeName: string,
    options: ErrorHandlingOptions = {}
  ) {
    this.enableErrorNotifications = options.enableErrorNotifications ?? true;
    this.enableReactionBatching = options.enableReactionBatching ?? true;
    this.debugMode = options.debugMode ?? false;

    // Make properties observable
    makeObservable(this, {
      errorOnLoad: observable,
      errorOnCreate: observable,
      errorOnUpdate: observable,
      errorOnDelete: observable,
      errorOnSubmit: observable,
      generalError: observable,
      clearError: action,
      clearAllErrors: action,
      hasError: computed,
      hasLoadError: computed,
      hasCRUDError: computed,
      hasSubmitError: computed,
      allErrors: computed,
    });

    this.initializeErrorHandling();
  }

  /**
   * Initialize error handling reactions - batched for performance
   */
  private initializeErrorHandling(): void {
    if (!this.enableReactionBatching) return;

    // Create reaction manager for batched error handling
    this.reactionManager = new ReactionManager(this.debugMode);
    this.reactionManager.createReactionBatch(`${this.storeName}-errors`, {
      delay: 50, // Batch error handling with 50ms delay
      maxWait: 200, // Maximum wait before forcing execution
      trailing: true
    });

    // Add all error reactions to the batch
    this.reactionManager.addToBatch(
      `${this.storeName}-errors`,
      'all-errors',
      () => ({
        load: this.errorOnLoad,
        create: this.errorOnCreate,
        update: this.errorOnUpdate,
        delete: this.errorOnDelete,
        submit: this.errorOnSubmit,
        general: this.generalError
      }),
      (current, previous) => {
        // Handle any new errors in batch
        Object.entries(current).forEach(([operation, error]) => {
          const prevError = previous?.[operation];
          if (error && error !== prevError) {
            this.handleError(error);
          }
        });
      },
      { fireImmediately: true }
    );

    this.reactionManager.activateBatch(`${this.storeName}-errors`);
    this.errorReactionsBatchDispose = () => {
      this.reactionManager?.disposeBatch(`${this.storeName}-errors`);
    };
  }

  /**
   * Central error handler - matches ServiceStore pattern
   */
  private handleError(error: ViewStoreError): void {
    console.error(`Error in ViewStore ${this.storeName}:`, error);

    // Handle authentication errors
    if (error.name === "NotAuthenticated") {
      this.handleNotAuthenticated(error.message);
    }

    // Handle not found errors
    if (error.name === "NotFound") {
      console.warn(`Resource not found in ${this.storeName}:`, error.message);
    }

    // Show error notifications if enabled
    if (this.enableErrorNotifications) {
      this.handleErrorNotification(error.name, error.message);
    }
  }

  /**
   * Handle authentication errors - matches ServiceStore approach
   */
  private handleNotAuthenticated(message: string): void {
    if (this.rootStore.routerStore && this.rootStore.authStore) {
      // Let the auth store handle the redirect
      console.warn(`Authentication error in ${this.storeName}:`, message);
    }
  }

  /**
   * Handle error notifications - matches ServiceStore pattern
   */
  private handleErrorNotification(name: string, message: string): void {
    if (this.rootStore.notificationStore) {
      this.rootStore.notificationStore.addNotification({
        message: `${this.storeName}: ${message}`,
        options: { variant: "warning" },
      });
    }
  }

  /**
   * Set error for specific operation
   */
  setError(operation: 'load' | 'create' | 'update' | 'delete' | 'submit' | 'general', error: Error | string, context?: Record<string, unknown>): void {
    const viewStoreError: ViewStoreError = {
      name: error instanceof Error ? error.name : 'ViewStoreError',
      message: error instanceof Error ? error.message : error,
      operation,
      timestamp: Date.now(),
      context
    };

    switch (operation) {
      case 'load':
        this.errorOnLoad = viewStoreError;
        break;
      case 'create':
        this.errorOnCreate = viewStoreError;
        break;
      case 'update':
        this.errorOnUpdate = viewStoreError;
        break;
      case 'delete':
        this.errorOnDelete = viewStoreError;
        break;
      case 'submit':
        this.errorOnSubmit = viewStoreError;
        break;
      case 'general':
        this.generalError = viewStoreError;
        break;
    }
  }

  /**
   * Clear specific error
   */
  clearError(operation: 'load' | 'create' | 'update' | 'delete' | 'submit' | 'general'): void {
    switch (operation) {
      case 'load':
        this.errorOnLoad = null;
        break;
      case 'create':
        this.errorOnCreate = null;
        break;
      case 'update':
        this.errorOnUpdate = null;
        break;
      case 'delete':
        this.errorOnDelete = null;
        break;
      case 'submit':
        this.errorOnSubmit = null;
        break;
      case 'general':
        this.generalError = null;
        break;
    }
  }

  /**
   * Clear all errors
   */
  clearAllErrors(): void {
    this.errorOnLoad = null;
    this.errorOnCreate = null;
    this.errorOnUpdate = null;
    this.errorOnDelete = null;
    this.errorOnSubmit = null;
    this.generalError = null;
  }

  /**
   * Computed property - check if store has any errors
   */
  get hasError(): boolean {
    return !!(
      this.errorOnLoad ||
      this.errorOnCreate ||
      this.errorOnUpdate ||
      this.errorOnDelete ||
      this.errorOnSubmit ||
      this.generalError
    );
  }

  /**
   * Computed property - check if store has load errors
   */
  get hasLoadError(): boolean {
    return !!this.errorOnLoad;
  }

  /**
   * Computed property - check if store has CRUD operation errors
   */
  get hasCRUDError(): boolean {
    return !!(
      this.errorOnCreate ||
      this.errorOnUpdate ||
      this.errorOnDelete
    );
  }

  /**
   * Computed property - check if store has submit errors (form submissions)
   */
  get hasSubmitError(): boolean {
    return !!this.errorOnSubmit;
  }

  /**
   * Get all current errors
   */
  get allErrors(): ViewStoreError[] {
    return [
      this.errorOnLoad,
      this.errorOnCreate,
      this.errorOnUpdate,
      this.errorOnDelete,
      this.errorOnSubmit,
      this.generalError
    ].filter(Boolean) as ViewStoreError[];
  }

  /**
   * Dispose error handling resources
   */
  dispose(): void {
    if (this.errorReactionsBatchDispose) {
      this.errorReactionsBatchDispose();
      this.errorReactionsBatchDispose = null;
    }

    if (this.reactionManager) {
      this.reactionManager.disposeAll();
      this.reactionManager = null;
    }

    this.clearAllErrors();
  }

  /**
   * Utility method to wrap async operations with error handling
   */
  async withErrorHandling<T>(
    operation: 'load' | 'create' | 'update' | 'delete' | 'submit' | 'general',
    asyncOperation: () => Promise<T>,
    context?: Record<string, unknown>
  ): Promise<T> {
    // Clear previous error for this operation
    this.clearError(operation);

    try {
      return await asyncOperation();
    } catch (error) {
      this.setError(operation, error as Error, context);
      throw error;
    }
  }
}

/**
 * Helper function to easily add error handling to ViewStore classes
 */
export function withErrorHandling(
  target: new (...args: unknown[]) => unknown,
  storeName: string,
  options?: ErrorHandlingOptions
) {
  return class extends target {
    errorHandler: ViewStoreErrorHandler;

    constructor(...args: unknown[]) {
      super(...args);
      this.errorHandler = new ViewStoreErrorHandler(
        (this as any).rootStore,
        storeName,
        options
      );

      // Expose error handler properties directly on the store for convenience
      Object.defineProperty(this, 'hasError', {
        get: () => this.errorHandler.hasError
      });
      Object.defineProperty(this, 'hasLoadError', {
        get: () => this.errorHandler.hasLoadError
      });
      Object.defineProperty(this, 'hasCRUDError', {
        get: () => this.errorHandler.hasCRUDError
      });
      Object.defineProperty(this, 'hasSubmitError', {
        get: () => this.errorHandler.hasSubmitError
      });
      Object.defineProperty(this, 'allErrors', {
        get: () => this.errorHandler.allErrors
      });
    }

    dispose() {
      if (this.errorHandler) {
        this.errorHandler.dispose();
      }
      if (super.dispose) {
        super.dispose();
      }
    }
  };
}